/**
 * Blog Post Entity Schemas
 *
 * Marketing blog domain — platform-global, NOT per-school. A post carries a
 * globally unique slug, a title, an excerpt, and a hero image alongside its
 * body. Two-status version model (DRAFT / PUBLISHED): editing a published post
 * produces a draft, and the published version keeps serving until the draft is
 * published over it. The slug stays editable after publish (SCH-2170); a
 * rename makes the old /blog/<slug> URL 404, with no redirect.
 *
 * The body is BlockNote block-array JSON. `BlogPostContentSchema` below is the
 * SECURITY BOUNDARY for that JSON, not a convenience validator:
 *
 *   - Block `type` is ALLOWLISTED (`BLOG_BLOCK_TYPES`). An unknown type is
 *     rejected, never stripped — silently dropping blocks loses an author's
 *     work, and silently keeping them would let a client invent a block the
 *     renderer treats specially.
 *   - Every URL-bearing prop and every inline link `href` must parse as an
 *     absolute `https:` URL. Rejected, never coerced — `javascript:`,
 *     `data:`, and `http:` all fail. BlockNote does not protocol-validate
 *     image/file `url` props itself.
 *   - Props are FLAT primitives (string / number / boolean). Nested objects and
 *     arrays in props are rejected, so there is no unvalidated sub-tree riding
 *     through a prop bag.
 * Parsing is non-destructive: no defaults, no transforms, no stripping. A valid
 * document round-trips through `BlogPostContentSchema.parse` unchanged, so
 * stored JSON is byte-comparable with what the editor produced.
 *
 * COLUMN NESTING IS BOUNDED (R16). `columnList` appears only at the root and
 * holds two or more `column`s; `column` appears only inside a `columnList` and
 * never holds a `columnList`, so column grids cannot nest. Every other nesting
 * stays as permissive as it was. A column's `width` must pass the shared bounds
 * in `./column-width` (KTD9) — the blog's first per-type prop rule.
 *
 * KEEPING THE EDITOR IN SYNC: the admin editor must build its BlockNote schema
 * from exactly `BLOG_BLOCK_TYPES`. A block the editor can insert but this list
 * omits fails validation at save time, which reads to the author as a lost
 * post. `table`, `video`, `audio`, and `file` are deliberately absent from v1 —
 * add each one here together with its content shape before enabling it in the
 * editor.
 *
 * A COMPONENT BLOCK (`BLOG_COMPONENT_BLOCK_TYPES`) is keyed in three places,
 * each typed `Record<BlogComponentBlockType, …>` so a missing entry is a compile
 * error (SCH-2162 KTD6): the editor schema's spec map, the editor's slash-menu
 * entry table, and the reader's component registry. They live in three modules
 * because they belong to three runtime graphs — server bake, client editor,
 * reader. Add the type here first and deploy it before any editor can produce it.
 *
 * See docs/_archive/2026-10-17-plans-sweep/plans/2026-07-31-004-feat-marketing-blog-authoring-plan.md.
 */

import { z } from 'zod'

import { BlogCategoryRefSchema } from './blog-category'
import { COLUMN_WIDTH_MESSAGE, isColumnWidth } from './column-width'

// ============================================
// URL constraint — https only, parsed not regexed
// ============================================

/**
 * The https predicate lives in `../../security/safe-url` now — the chat
 * moderation surface needed the same check over provider-supplied urls, and
 * two copies of "is this safe to put in an href" is exactly the pair that
 * drifts. Re-exported here so every existing importer of this module (and of
 * the root barrel) is unaffected.
 */
import { isHttpsUrl } from '../../security/safe-url'
export { isHttpsUrl }

export const HTTPS_URL_MESSAGE = 'URL must be an absolute https: URL'

/** Absolute https URL. Used for hero images and any URL-bearing block prop. */
export const HttpsUrlSchema = z.string().max(2000).refine(isHttpsUrl, { message: HTTPS_URL_MESSAGE })

// ============================================
// Block allowlist
// ============================================

/**
 * The closed set of block types a blog post may contain: exactly the BlockNote
 * default blocks the editor exposes. See the sync warning in the module header
 * before changing it.
 *
 * `alignedImage` — a custom block carrying float-left/right layout — was removed
 * along with the second image block it forced the editor to carry. Two blocks
 * both called "image", one of them with a hand-rolled control bar and no
 * drag-and-drop, was the confusion; BlockNote's default `image` block already
 * brings drop, paste, the slash command, an upload panel, resize and caption.
 * The only capability lost is text WRAPPING around a floated image, which
 * Notion, Medium and Ghost do not offer either.
 */
const BLOG_DEFAULT_BLOCK_TYPES = [
  'paragraph',
  'heading',
  'quote',
  'bulletListItem',
  'numberedListItem',
  'checkListItem',
  'codeBlock',
  'image'
] as const

/**
 * Component blocks: a placeholder in the stored HTML that the reader swaps for a
 * code-owned React component (SCH-2162). A CLOSED list — see the header for the
 * three places each member is keyed. `bookDemoCta` is the "Book a demo" banner;
 * its copy, image and link live in code, so it carries no author props.
 */
export const BLOG_COMPONENT_BLOCK_TYPES = ['bookDemoCta'] as const
export type BlogComponentBlockType = (typeof BLOG_COMPONENT_BLOCK_TYPES)[number]

/** `@blocknote/xl-multi-column`'s two block types. Nesting rules: see the header. */
const BLOG_COLUMN_BLOCK_TYPES = ['columnList', 'column'] as const

export const BLOG_BLOCK_TYPES = [
  ...BLOG_DEFAULT_BLOCK_TYPES,
  ...BLOG_COMPONENT_BLOCK_TYPES,
  ...BLOG_COLUMN_BLOCK_TYPES
] as const
export const BlogBlockTypeSchema = z.enum(BLOG_BLOCK_TYPES)
export type BlogBlockType = z.infer<typeof BlogBlockTypeSchema>

/**
 * Prop keys whose value must be an https URL. Matched exactly, plus any key
 * ending in `url` / `Url` so a prop added later inherits the constraint by
 * naming convention rather than by someone remembering this list.
 */
const EXPLICIT_URL_PROP_KEYS = new Set(['url', 'href', 'src'])

function isUrlPropKey(key: string): boolean {
  return EXPLICIT_URL_PROP_KEYS.has(key) || /url$/i.test(key)
}

// ============================================
// Inline content
// ============================================

/** BlockNote style marks — `bold: true`, `textColor: 'red'`, and friends. */
export const BlogInlineStylesSchema = z.record(z.string(), z.union([z.string(), z.boolean()]))
export type BlogInlineStyles = z.infer<typeof BlogInlineStylesSchema>

export const BlogStyledTextSchema = z
  .object({
    type: z.literal('text'),
    text: z.string().max(50_000),
    styles: BlogInlineStylesSchema.optional()
  })
  .strict()
export type BlogStyledText = z.infer<typeof BlogStyledTextSchema>

/**
 * Inline link. `href` is https-only for the same reason image `url` is: a
 * `javascript:` href in stored content is a stored-XSS payload the renderer
 * would faithfully reproduce.
 */
export const BlogLinkSchema = z
  .object({
    type: z.literal('link'),
    href: HttpsUrlSchema,
    content: z.array(BlogStyledTextSchema)
  })
  .strict()
export type BlogLink = z.infer<typeof BlogLinkSchema>

export const BlogInlineContentSchema = z.discriminatedUnion('type', [BlogStyledTextSchema, BlogLinkSchema])
export type BlogInlineContent = z.infer<typeof BlogInlineContentSchema>

// ============================================
// Block
// ============================================

/**
 * Block props are FLAT primitives only. BlockNote's own prop bags are flat
 * (`textAlignment`, `backgroundColor`, `level`, `url`, `caption`, …), so this
 * costs nothing and removes an entire class of unvalidated nested payload.
 */
export const BlogBlockPropsSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
export type BlogBlockProps = z.infer<typeof BlogBlockPropsSchema>

export interface BlogBlock {
  id: string
  type: BlogBlockType
  props?: BlogBlockProps
  content?: BlogInlineContent[]
  children?: BlogBlock[]
}

/** The fewest columns a `columnList` holds — xl-multi-column's own `column column+`. */
const BLOG_MIN_COLUMNS = 2

/** R16 on one child: where a column type may sit relative to its parent. */
function columnChildIssue(parentType: BlogBlockType, childType: BlogBlockType): string | null {
  if (childType === 'columnList') return 'A columnList belongs at the root of the post; it may not nest'
  if (childType === 'column' && parentType !== 'columnList') return 'A column may only sit inside a columnList'
  if (parentType === 'columnList' && childType !== 'column') return 'A columnList holds only column blocks'
  return null
}

/** R16 and R18 for one block: its children's placement, its column count, its width. */
function addColumnIssues(block: BlogBlock, ctx: z.RefinementCtx): void {
  const children = block.children ?? []

  children.forEach((child, index) => {
    const message = columnChildIssue(block.type, child.type)
    if (message) ctx.addIssue({ code: 'custom', path: ['children', index, 'type'], message })
  })

  if (block.type === 'columnList' && children.length < BLOG_MIN_COLUMNS) {
    ctx.addIssue({ code: 'custom', path: ['children'], message: 'A columnList holds at least two columns' })
  }

  const width = block.props?.width
  if (block.type === 'column' && width !== undefined && !isColumnWidth(width)) {
    ctx.addIssue({ code: 'custom', path: ['props', 'width'], message: COLUMN_WIDTH_MESSAGE })
  }
}

/**
 * A single block. Recursive via `z.lazy` on `children` (nested list items).
 * The cast pins the public type to the hand-written `BlogBlock` so declaration
 * emit stays concrete — a plain `z.infer` over the cycle widens to `unknown`.
 */
export const BlogBlockSchema: z.ZodType<BlogBlock> = z.lazy(() =>
  z
    .object({
      id: z.string().min(1).max(128),
      type: BlogBlockTypeSchema,
      props: BlogBlockPropsSchema.optional(),
      content: z.array(BlogInlineContentSchema).optional(),
      children: z.array(BlogBlockSchema).optional()
    })
    .strict()
    .superRefine((block, ctx) => {
      const props = block.props ?? {}

      addColumnIssues(block, ctx)

      for (const [key, value] of Object.entries(props)) {
        if (!isUrlPropKey(key)) continue
        // An empty string is BlockNote's "no file chosen yet" state on a
        // freshly inserted image block; a draft save must not fail on it.
        if (value === '') continue
        if (typeof value !== 'string' || !isHttpsUrl(value)) {
          ctx.addIssue({
            code: 'custom',
            path: ['props', key],
            message: `Block prop "${key}" must be an absolute https: URL`
          })
        }
      }
    })
) as z.ZodType<BlogBlock>

/** Guard against a runaway paste; well past any real post. */
export const BLOG_MAX_BLOCKS = 2000

/**
 * A post body: the BlockNote document, which is a flat array of root blocks. A
 * `column` at the root is rejected here; everywhere else the block refinement
 * above places it.
 */
export const BlogPostContentSchema = z
  .array(BlogBlockSchema)
  .max(BLOG_MAX_BLOCKS)
  .superRefine((blocks, ctx) => {
    blocks.forEach((block, index) => {
      if (block.type === 'column') {
        ctx.addIssue({ code: 'custom', path: [index, 'type'], message: 'A column may only sit inside a columnList' })
      }
    })
  })
export type BlogPostContent = z.infer<typeof BlogPostContentSchema>

// ============================================
// BlogPost + BlogPostVersion entity contracts
// ============================================

export const BlogPostVersionStatusSchema = z.enum(['DRAFT', 'PUBLISHED'])
export type BlogPostVersionStatus = z.infer<typeof BlogPostVersionStatusSchema>

export const BlogPostVersionSchema = z.object({
  publicId: z.string(),
  status: BlogPostVersionStatusSchema,
  content: BlogPostContentSchema,
  /**
   * Sanitized HTML materialised at publish time. Null on drafts and on any
   * published version stored before the client supplied HTML — consumers fall
   * back to rendering `content` rather than treating null as an error.
   */
  renderedHtml: z.string().nullable(),
  createdAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable()
})
export type BlogPostVersion = z.infer<typeof BlogPostVersionSchema>

/** Fields shared by every representation of a post. */
export const BlogPostBaseSchema = z.object({
  publicId: z.string(),
  slug: z.string(),
  title: z.string(),
  excerpt: z.string().nullable(),
  heroImageUrl: z.string().nullable(),
  /** At most one category per post; null when the post has none (SCH-2108). */
  category: BlogCategoryRefSchema.nullable()
})
export type BlogPostBase = z.infer<typeof BlogPostBaseSchema>

/** Admin-facing post with both versions resolved. */
export const BlogPostSchema = BlogPostBaseSchema.extend({
  draftVersion: BlogPostVersionSchema.nullable(),
  publishedVersion: BlogPostVersionSchema.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
})
export type BlogPost = z.infer<typeof BlogPostSchema>

/**
 * Public-facing post — the published version only, flattened.
 *
 * `content` is parsed LOOSELY here, on purpose (SCH-2162 KTD11). Public pages
 * render `renderedHtml` and never read the block JSON, so a strict parse only
 * turns deploy skew — a web build older than the vocabulary that stored the
 * post, or a rolled-back web deploy — into a 500 on a live post. The admin and
 * write paths keep `BlogPostContentSchema`, the real security boundary.
 */
export const PublicBlogPostSchema = BlogPostBaseSchema.extend({
  content: z.array(z.unknown()),
  renderedHtml: z.string().nullable(),
  publishedAt: z.string().datetime()
})
export type PublicBlogPost = z.infer<typeof PublicBlogPostSchema>

/** Public index row — no body. */
export const PublicBlogPostSummarySchema = BlogPostBaseSchema.extend({
  publishedAt: z.string().datetime()
})
export type PublicBlogPostSummary = z.infer<typeof PublicBlogPostSummarySchema>
